import { useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for managing focus on validation errors in calculators.
 * Automatically scrolls to and focuses on the first error field in logical order,
 * but only when appropriate (not when user is actively typing).
 */
const useErrorFocus = (errors, fieldOrder = []) => {
  const lastErrorCountRef = useRef(0);

  const focusElementById = useCallback((fieldName) => {
    // Common ID patterns used in calculators
    const possibleIds = [
      fieldName,
      `${fieldName}-input`,
      `abx-${fieldName}`,
      `crc-${fieldName}`,
      `ibw-${fieldName}`,
      `bmi-${fieldName}`,
      `lbw-${fieldName}`,
      `pka-${fieldName}`,
      `ag-${fieldName}`,
      `calc-${fieldName}`,
      `gfr-${fieldName}`,
      `bsa-${fieldName}`
    ];

    // For height field, also try feet/inches variants
    if (fieldName === 'height') {
      possibleIds.push(
        `ag-height-feet`,
        `abx-height-feet`,
        `calc-height-feet`,
        `crc-height-feet`,
        `ibw-height-feet`,
        `bmi-height-feet`,
        `lbw-height-feet`,
        `pka-height-feet`,
        `gfr-height-feet`,
        `bsa-height-feet`
      );
    }

    for (const id of possibleIds) {
      const element = document.getElementById(id);
      if (element) {

        if (typeof element.scrollIntoView === 'function') {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }

        // Focus the element after a short delay to ensure scroll completes
        setTimeout(() => {
          if (element.focus && typeof element.focus === 'function') {
            element.focus();
          }
        }, 100);

        return;
      }
    }

    // If no element found by ID, try to find by data attribute or class
    const elementByDataField = document.querySelector(`[data-field="${fieldName}"]`);
    if (elementByDataField) {
      elementByDataField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        if (elementByDataField.focus) elementByDataField.focus();
      }, 100);
    }
  }, []);

  const focusFirstError = useCallback(() => {
    if (!errors || Object.keys(errors).length === 0) return;

    const firstErrorField = fieldOrder.find(fieldName =>
      errors[fieldName] && errors[fieldName].trim() !== ''
    );

    if (!firstErrorField) {
      const errorKeys = Object.keys(errors).filter(key => errors[key] && errors[key].trim() !== '');
      if (errorKeys.length > 0) {
        focusElementById(errorKeys[0]);
      }
      return;
    }

    focusElementById(firstErrorField);
  }, [errors, fieldOrder, focusElementById]);

  // Automatically focus on first error when errors change, but be smart about it
  useEffect(() => {
    if (!errors || Object.keys(errors).length === 0) {
      lastErrorCountRef.current = 0;
      return;
    }

    const currentErrorCount = Object.keys(errors).filter(key =>
      errors[key] && errors[key].trim() !== ''
    ).length;

    const previousErrorCount = lastErrorCountRef.current;
    lastErrorCountRef.current = currentErrorCount;

    // Determine if we should auto-focus:
    // 1. If error count increased (new errors appeared) - likely from validation
    // 2. If this is the first time errors appeared (initial validation)
    // 3. But NOT if error count decreased (user is fixing errors by typing)
    const shouldAutoFocus =
      currentErrorCount > 0 && (
        previousErrorCount === 0 ||  // First time errors appeared
        currentErrorCount > previousErrorCount // New errors added
      );

    if (shouldAutoFocus) {
      // Small delay to ensure DOM is updated with error states
      const timer = setTimeout(() => {
        focusFirstError();
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [errors, focusFirstError]);

  return focusFirstError;
};

export default useErrorFocus;
